import { runAudioStore } from './audioIdb';
import { notifyAudioLibraryChanged } from './audioLibraryEvents';
import { createTrackId } from './audioPlaylistStorage';
import { sanitizeTileColor, tileLabelForSave } from './audioTileAppearance';
import {
  cloudDeleteCategory,
  cloudListCategories,
  cloudSaveBrowsePrefs,
  cloudSaveCategory,
  hasCloudAudioLibrary,
} from './audioPlayerLibraryApi';
import {
  deleteLocalCategory,
  mirrorCategoriesToLocal,
  mirrorCategoryToLocal,
} from './audioPlayerLocalMirror';

const STORE = 'browseCategories';
const COLLAPSE_KEY = 'bahlus-audio-category-collapsed';
const HIDDEN_KEY = 'bahlus-audio-hidden-categories';

/** Signed-in browse UI prefs from account sync (hide / collapse). */
let cloudBrowsePrefs = null;

export function clearCloudBrowsePrefsCache() {
  cloudBrowsePrefs = null;
}

function normalizeBrowsePrefs(prefs) {
  return {
    hiddenCategoryKeys: Array.isArray(prefs?.hiddenCategoryKeys)
      ? prefs.hiddenCategoryKeys.map(String)
      : [],
    hiddenStationKeys: Array.isArray(prefs?.hiddenStationKeys)
      ? prefs.hiddenStationKeys.map(String)
      : [],
    categoryCollapsed:
      prefs?.categoryCollapsed && typeof prefs.categoryCollapsed === 'object'
        ? { ...prefs.categoryCollapsed }
        : {},
  };
}

export function applySyncedBrowsePrefs(prefs) {
  if (!prefs || typeof prefs !== 'object') {
    cloudBrowsePrefs = normalizeBrowsePrefs(null);
    return;
  }
  const { initialized: _init, ...rest } = prefs;
  cloudBrowsePrefs = normalizeBrowsePrefs(rest);
}

export function browsePrefsNeedsLocalMigration(prefs) {
  return prefs && prefs.initialized === false;
}

export function browsePrefsIsEmpty(prefs) {
  const p = normalizeBrowsePrefs(prefs);
  return (
    !p.hiddenCategoryKeys.length &&
    !p.hiddenStationKeys.length &&
    !Object.keys(p.categoryCollapsed).length
  );
}

function readHiddenCategoryKeysLocal() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = JSON.parse(localStorage.getItem(HIDDEN_KEY) || '[]');
    return Array.isArray(raw) ? raw.map(String) : [];
  } catch {
    return [];
  }
}

export function readHiddenCategoryKeysLocalOnly() {
  return readHiddenCategoryKeysLocal();
}

export function readHiddenCategoryKeys() {
  if (cloudBrowsePrefs) return cloudBrowsePrefs.hiddenCategoryKeys;
  return readHiddenCategoryKeysLocal();
}

export function isCategoryHidden(categoryKey) {
  if (!categoryKey) return false;
  return readHiddenCategoryKeys().includes(String(categoryKey));
}

export function setCategoryHidden(categoryKey, hidden) {
  if (!categoryKey) return;
  const key = String(categoryKey);
  if (cloudBrowsePrefs) {
    const set = new Set(cloudBrowsePrefs.hiddenCategoryKeys);
    if (hidden) set.add(key);
    else set.delete(key);
    cloudBrowsePrefs = {
      ...cloudBrowsePrefs,
      hiddenCategoryKeys: [...set],
    };
    void persistCloudBrowsePrefs();
    return;
  }
  if (typeof localStorage === 'undefined') return;
  const set = new Set(readHiddenCategoryKeysLocal());
  if (hidden) set.add(key);
  else set.delete(key);
  localStorage.setItem(HIDDEN_KEY, JSON.stringify([...set]));
}

export function clearHiddenCategories() {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(HIDDEN_KEY);
}

const HIDDEN_STATIONS_KEY = 'bahlus-audio-hidden-stations';

function readHiddenStationKeysLocal() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = JSON.parse(localStorage.getItem(HIDDEN_STATIONS_KEY) || '[]');
    return Array.isArray(raw) ? raw.map(String) : [];
  } catch {
    return [];
  }
}

export function readHiddenStationKeysLocalOnly() {
  return readHiddenStationKeysLocal();
}

export function readHiddenStationKeys() {
  if (cloudBrowsePrefs) return cloudBrowsePrefs.hiddenStationKeys;
  return readHiddenStationKeysLocal();
}

export function isStationHidden(stationKey) {
  if (!stationKey) return false;
  return readHiddenStationKeys().includes(String(stationKey));
}

export function setStationHidden(stationKey, hidden) {
  if (!stationKey) return;
  const key = String(stationKey);
  if (cloudBrowsePrefs) {
    const set = new Set(cloudBrowsePrefs.hiddenStationKeys);
    if (hidden) set.add(key);
    else set.delete(key);
    cloudBrowsePrefs = {
      ...cloudBrowsePrefs,
      hiddenStationKeys: [...set],
    };
    void persistCloudBrowsePrefs();
    return;
  }
  if (typeof localStorage === 'undefined') return;
  const set = new Set(readHiddenStationKeysLocal());
  if (hidden) set.add(key);
  else set.delete(key);
  localStorage.setItem(HIDDEN_STATIONS_KEY, JSON.stringify([...set]));
}

export function clearHiddenStations() {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(HIDDEN_STATIONS_KEY);
}

export function clearAllBrowseHides() {
  if (cloudBrowsePrefs) {
    cloudBrowsePrefs = {
      ...cloudBrowsePrefs,
      hiddenCategoryKeys: [],
      hiddenStationKeys: [],
    };
    void persistCloudBrowsePrefs();
    return;
  }
  clearHiddenCategories();
  clearHiddenStations();
}

export function catalogStationHideKey(genreId, streamId) {
  return `station:catalog:${genreId}:${streamId}`;
}

export function customStationHideKey(stationId) {
  return `station:custom:${stationId}`;
}

function readCategoryCollapseStateLocal() {
  if (typeof localStorage === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '{}');
  } catch {
    return {};
  }
}

export function readCategoryCollapseStateLocalOnly() {
  return readCategoryCollapseStateLocal();
}

export function readCategoryCollapseState() {
  if (cloudBrowsePrefs) return { ...cloudBrowsePrefs.categoryCollapsed };
  return readCategoryCollapseStateLocal();
}

export function writeCategoryCollapseState(map) {
  if (cloudBrowsePrefs) {
    cloudBrowsePrefs = {
      ...cloudBrowsePrefs,
      categoryCollapsed: map && typeof map === 'object' ? { ...map } : {},
    };
    void persistCloudBrowsePrefs();
    return;
  }
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(COLLAPSE_KEY, JSON.stringify(map || {}));
}

let persistBrowsePrefsPromise = null;

async function persistCloudBrowsePrefs() {
  if (!cloudBrowsePrefs) return;
  const snapshot = normalizeBrowsePrefs(cloudBrowsePrefs);
  persistBrowsePrefsPromise = (async () => {
    try {
      if (!(await hasCloudAudioLibrary())) return;
      const saved = await cloudSaveBrowsePrefs(snapshot);
      applySyncedBrowsePrefs(saved);
    } catch (err) {
      console.warn('[audio-player] Could not save browse prefs to account', err);
    }
  })();
  await persistBrowsePrefsPromise;
}

export function buildBrowsePrefsFromLocalStorage() {
  return {
    hiddenCategoryKeys: readHiddenCategoryKeysLocalOnly(),
    hiddenStationKeys: readHiddenStationKeysLocalOnly(),
    categoryCollapsed: readCategoryCollapseStateLocalOnly(),
  };
}

/** @returns {boolean} true when section is collapsed (hidden) */
export function isCategoryCollapsed(categoryKey, defaultCollapsed = false) {
  const map = readCategoryCollapseState();
  if (map[categoryKey] === undefined) return defaultCollapsed;
  return Boolean(map[categoryKey]);
}

export function setCategoryCollapsed(categoryKey, collapsed) {
  const map = readCategoryCollapseState();
  map[categoryKey] = Boolean(collapsed);
  writeCategoryCollapseState(map);
}

export function builtinCategoryKey(genreId) {
  return `builtin:${genreId}`;
}

async function readBrowseCategoriesFromIdb() {
  return runAudioStore(STORE, 'readonly', (store) => {
    const req = store.getAll();
    return new Promise((resolve, reject) => {
      req.onsuccess = () => {
        const rows = (req.result || []).slice();
        rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        resolve(rows);
      };
      req.onerror = () => reject(req.error);
    });
  });
}

export async function listBrowseCategories() {
  if (await hasCloudAudioLibrary()) {
    const rows = await cloudListCategories();
    const sorted = (rows || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    await mirrorCategoriesToLocal(sorted);
    return sorted;
  }
  return readBrowseCategoriesFromIdb();
}

export async function saveBrowseCategory({ id, name, playlistIds, tileLabel, tileColor }) {
  const categoryName = String(name || 'New category').trim() || 'New category';
  const payload = {
    id: id || undefined,
    name: categoryName,
    playlistIds: Array.isArray(playlistIds) ? [...new Set(playlistIds.map(String))] : [],
    tileLabel: tileLabelForSave(tileLabel, categoryName),
    tileColor: sanitizeTileColor(tileColor),
  };

  if (await hasCloudAudioLibrary()) {
    const saved = await cloudSaveCategory(payload);
    await mirrorCategoryToLocal(saved);
    notifyAudioLibraryChanged({ store: 'browseCategories', id: saved.id, cloud: true });
    return saved;
  }

  const entry = {
    ...payload,
    id: payload.id || createTrackId(),
    savedAt: Date.now(),
  };
  await runAudioStore(STORE, 'readwrite', (store) => store.put(entry));
  notifyAudioLibraryChanged({ store: 'browseCategories', id: entry.id });
  return entry;
}

export async function deleteBrowseCategory(id) {
  if (!id) return;
  if (await hasCloudAudioLibrary()) {
    await cloudDeleteCategory(id);
    await deleteLocalCategory(id);
    notifyAudioLibraryChanged({ store: 'browseCategories', id, deleted: true, cloud: true });
    return;
  }
  await runAudioStore(STORE, 'readwrite', (store) => store.delete(String(id)));
  notifyAudioLibraryChanged({ store: 'browseCategories', id, deleted: true });
}

export async function listBrowseCategoriesLocalOnly() {
  return readBrowseCategoriesFromIdb();
}

export async function addPlaylistToBrowseCategory(categoryId, playlistId) {
  if (!categoryId || !playlistId) return null;
  const rows = await listBrowseCategories();
  const row = rows.find((r) => r.id === categoryId);
  if (!row) return null;
  const playlistIds = [...new Set([...(row.playlistIds || []), String(playlistId)])];
  return saveBrowseCategory({
    id: row.id,
    name: row.name,
    playlistIds,
    tileLabel: row.tileLabel,
    tileColor: row.tileColor,
  });
}

export async function removePlaylistFromBrowseCategory(categoryId, playlistId) {
  if (!categoryId || !playlistId) return null;
  const rows = await listBrowseCategories();
  const row = rows.find((r) => r.id === categoryId);
  if (!row) return null;
  const playlistIds = (row.playlistIds || []).filter((pid) => pid !== String(playlistId));
  return saveBrowseCategory({
    id: row.id,
    name: row.name,
    playlistIds,
    tileLabel: row.tileLabel,
    tileColor: row.tileColor,
  });
}

export function customCategoryKey(id) {
  return `custom:${id}`;
}

export const MY_PLAYLISTS_CATEGORY_KEY = 'section:my-playlists';
